import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { kolicina, nivoKratko } from '../lib/format'
import { PrijemCijene } from './PrijemCijene'
import type { Nalog } from '../lib/tipovi'

/** Prijem — telefonski ekran, prvi u aplikaciji.
 *
 *  Magacioner drži skener u jednoj ruci, često sa rukavicama, u hladnjači ili kraj kamiona
 *  pod suncem. Zato: scan polje je uvijek fokusirano i `inputmode="none"`, pa se tastatura
 *  ne diže (DR-3A); readout zauzima gornju polovinu i čita se sa 80 cm; u gornjoj polovini
 *  nema nijednog dodirnog elementa, da rukavica ne uništi skeniranje koje čita.
 *
 *  Uspjeh je tih — broj se promijeni. Izuzetak poplavi pola ekrana (DR-4A): nepoznat bar
 *  kod crnim, greška crvenim. Ponovljeno skeniranje istog koda dodaje jedno pakovanje
 *  (DR-3B).
 *
 *  Magacioner NE knjiži. Kad prebroji, predaje dokument kroz `predaj_kolicine` (DE-8);
 *  cijene sa fakture unosi neko na računaru. Prijem proknjižen bez cijena upisao bi
 *  vrijednost nula i pokvario prosječnu nabavnu cijenu (C6).
 */

type Stavka = {
  id: string
  artikalId: string
  sifra: string
  naziv: string
  jedinica: string
  nivo: string
  sadrziOsn: number
  pakovanja: number
  stanje: 'ceka' | 'sacuvano' | 'greska'
}

type Poplava =
  | { vrsta: 'nepoznat'; kod: string }
  | { vrsta: 'greska'; poruka: string }
  | null

type Partner = { id: string; naziv: string }
type Nacrt = { id: string; partnerNaziv: string | null }

export function Prijem({ nalog }: { nalog: Nalog | null }) {
  const [nacrt, postaviNacrt] = useState<Nacrt | null>(null)
  const [partneri, postaviPartnere] = useState<Partner[]>([])
  const [otvoreni, postaviOtvorene] = useState<{ id: string; partner: string | null; stavki: number }[]>([])
  // Prebrojani prijemi koji čekaju cijene sa fakture — drugi korak DE-8, na računaru.
  const [predati, postaviPredate] = useState<{ id: string; partner: string | null; stavki: number }[]>([])
  const [cijene, postaviCijene] = useState<{ id: string; partner: string | null } | null>(null)
  const [stavke, postaviStavke] = useState<Stavka[]>([])
  const [zadnjiId, postaviZadnjiId] = useState<string | null>(null)
  const [poplava, postaviPoplavu] = useState<Poplava>(null)
  const [kod, postaviKod] = useState('')
  const [svePrikazano, postaviSve] = useState(false)
  const [predaje, postaviPredaje] = useState(false)
  const [poruka, postaviPoruku] = useState<string | null>(null)
  const [ucitava, postaviUcitava] = useState(true)
  // Brojač koji se poveća poslije predaje, da se spisak otvorenih nacrta ponovo pročita iz
  // baze. Bez njega predat dokument ostaje na listi kao otvoren do osvježavanja stranice.
  const [osvjezi, postaviOsvjezi] = useState(0)

  const poljeRef = useRef<HTMLInputElement>(null)
  const smijeRaditi = nalog?.uloga === 'magacioner' || nalog?.uloga === 'admin'

  // Scan polje mora biti fokusirano stalno: skener „kuca" u ono što je fokusirano, pa svaki
  // izgubljen fokus znači izgubljeno skeniranje.
  const vratiFokus = useCallback(() => {
    if (nacrt && !svePrikazano) poljeRef.current?.focus()
  }, [nacrt, svePrikazano])

  useEffect(() => {
    vratiFokus()
  }, [vratiFokus, stavke, poplava])

  // Dobavljači i već započeti nacrti (DR-1A: „Moji otvoreni nacrti").
  useEffect(() => {
    let otkazano = false
    async function ucitaj() {
      const [odgPartneri, odgNacrti, odgPredati] = await Promise.all([
        supabase
          .from('partneri')
          .select('id, naziv')
          .eq('je_dobavljac', true)
          .eq('status', 'aktivan')
          .order('naziv'),
        supabase
          .from('dokumenti')
          .select('id, partner_id, partneri!dokumenti_partner_id_firma_id_fkey (naziv), stavke_dokumenta(count)')
          .eq('tip', 'prijem')
          .eq('status', 'nacrt')
          .is('kolicine_gotove_u', null)
          .order('kreiran', { ascending: false }),
        // Drugi korak DE-8: prebrojani prijemi koji čekaju cijene sa fakture.
        supabase
          .from('dokumenti')
          .select('id, partner_id, partneri!dokumenti_partner_id_firma_id_fkey (naziv), stavke_dokumenta(count)')
          .eq('tip', 'prijem')
          .eq('status', 'nacrt')
          .not('kolicine_gotove_u', 'is', null)
          .order('kolicine_gotove_u', { ascending: true }),
      ])
      if (otkazano) return

      if (odgPartneri.data) postaviPartnere(odgPartneri.data as Partner[])

      const uRedove = (redovi: unknown) =>
        (redovi as {
          id: string
          partneri: { naziv: string } | { naziv: string }[] | null
          stavke_dokumenta: { count: number }[]
        }[]).map((d) => ({
          id: d.id,
          partner: (Array.isArray(d.partneri) ? d.partneri[0] : d.partneri)?.naziv ?? null,
          stavki: d.stavke_dokumenta?.[0]?.count ?? 0,
        }))

      if (odgNacrti.data) postaviOtvorene(uRedove(odgNacrti.data))
      if (odgPredati.data) postaviPredate(uRedove(odgPredati.data))
      postaviUcitava(false)
    }
    void ucitaj()
    return () => {
      otkazano = true
    }
  }, [osvjezi])

  async function ucitajStavke(dokumentId: string) {
    const { data, error } = await supabase
      .from('stavke_dokumenta')
      .select(
        'id, artikal_id, kolicina_osn, kolicina_pakovanja, pakovanje_sadrzi_osn, sifra_snapshot, naziv_snapshot, jedinica_snapshot, pakovanja!stavke_dokumenta_pakovanje_id_firma_id_fkey (nivo)',
      )
      .eq('dokument_id', dokumentId)
      .order('redni_broj')

    if (error) {
      postaviPoruku(`Stavke se ne mogu učitati: ${error.message}`)
      return
    }
    const redovi = (data ?? []) as unknown as {
      id: string
      artikal_id: string
      kolicina_osn: string
      kolicina_pakovanja: string | null
      pakovanje_sadrzi_osn: string | null
      sifra_snapshot: string | null
      naziv_snapshot: string | null
      jedinica_snapshot: string | null
      pakovanja: { nivo: string } | { nivo: string }[] | null
    }[]
    postaviStavke(
      redovi.map((r) => ({
        id: r.id,
        artikalId: r.artikal_id,
        sifra: r.sifra_snapshot ?? '—',
        naziv: r.naziv_snapshot ?? '—',
        jedinica: r.jedinica_snapshot ?? '',
        nivo: (Array.isArray(r.pakovanja) ? r.pakovanja[0] : r.pakovanja)?.nivo ?? 'komad',
        sadrziOsn: Number(r.pakovanje_sadrzi_osn ?? 1),
        pakovanja: Number(r.kolicina_pakovanja ?? r.kolicina_osn),
        stanje: 'sacuvano' as const,
      })),
    )
  }

  async function zapocni(partnerId: string, partnerNaziv: string) {
    postaviPoruku(null)
    const { data, error } = await supabase
      .from('dokumenti')
      .insert({ firma_id: nalog!.firmaId, tip: 'prijem', partner_id: partnerId, kreirao: nalog!.korisnikId })
      .select('id')
      .single()

    if (error) {
      postaviPoruku(`Nacrt se ne može napraviti: ${error.message}`)
      return
    }
    postaviNacrt({ id: (data as { id: string }).id, partnerNaziv })
    postaviStavke([])
    postaviZadnjiId(null)
  }

  async function nastavi(dokumentId: string, partnerNaziv: string | null) {
    postaviPoruku(null)
    postaviNacrt({ id: dokumentId, partnerNaziv })
    await ucitajStavke(dokumentId)
    postaviZadnjiId(null)
  }

  /** Jedno skeniranje. Ponovljen isti kod dodaje jedno pakovanje (DR-3B). */
  async function skeniraj(barKod: string) {
    const trazeni = barKod.trim()
    if (trazeni === '' || !nacrt) return
    postaviKod('')
    postaviPoplavu(null)

    const { data, error } = await supabase
      .from('pakovanja')
      .select('id, nivo, sadrzi_osn, artikal_id, artikli!pakovanja_artikal_id_firma_id_fkey (sifra, naziv, osnovna_jedinica, status)')
      .eq('bar_kod', trazeni)
      .is('vazi_do', null)
      .maybeSingle()

    if (error) {
      postaviPoplavu({ vrsta: 'greska', poruka: error.message })
      return
    }
    if (!data) {
      postaviPoplavu({ vrsta: 'nepoznat', kod: trazeni })
      return
    }

    const p = data as unknown as {
      id: string
      nivo: string
      sadrzi_osn: string
      artikal_id: string
      artikli: { sifra: string; naziv: string; osnovna_jedinica: string; status: string }
        | { sifra: string; naziv: string; osnovna_jedinica: string; status: string }[]
        | null
    }
    const artikal = Array.isArray(p.artikli) ? p.artikli[0] : p.artikli
    if (!artikal) {
      postaviPoplavu({ vrsta: 'greska', poruka: 'Pakovanje nema artikal.' })
      return
    }
    if (artikal.status !== 'aktivan') {
      postaviPoplavu({ vrsta: 'greska', poruka: `${artikal.naziv} je neaktivan artikal.` })
      return
    }

    const sadrzi = Number(p.sadrzi_osn)
    const postojeca = stavke.find((s) => s.artikalId === p.artikal_id)

    if (postojeca) {
      const novaPakovanja = postojeca.pakovanja + 1
      postaviStavke((prethodne) =>
        prethodne.map((s) =>
          s.id === postojeca.id ? { ...s, pakovanja: novaPakovanja, stanje: 'ceka' } : s,
        ),
      )
      postaviZadnjiId(postojeca.id)

      const { error: greskaUpisa } = await supabase
        .from('stavke_dokumenta')
        .update({ kolicina_pakovanja: novaPakovanja, kolicina_osn: novaPakovanja * sadrzi })
        .eq('id', postojeca.id)

      postaviStavke((prethodne) =>
        prethodne.map((s) =>
          s.id === postojeca.id ? { ...s, stanje: greskaUpisa ? 'greska' : 'sacuvano' } : s,
        ),
      )
      if (greskaUpisa) postaviPoplavu({ vrsta: 'greska', poruka: greskaUpisa.message })
      return
    }

    const redniBroj = stavke.length + 1
    const { data: upisana, error: greskaUpisa } = await supabase
      .from('stavke_dokumenta')
      .insert({
        firma_id: nalog!.firmaId,
        dokument_id: nacrt.id,
        artikal_id: p.artikal_id,
        redni_broj: redniBroj,
        kolicina_pakovanja: 1,
        kolicina_osn: sadrzi,
        pakovanje_id: p.id,
        pakovanje_sadrzi_osn: sadrzi,
        sifra_snapshot: artikal.sifra,
        naziv_snapshot: artikal.naziv,
        jedinica_snapshot: artikal.osnovna_jedinica,
      })
      .select('id')
      .single()

    if (greskaUpisa || !upisana) {
      postaviPoplavu({ vrsta: 'greska', poruka: greskaUpisa?.message ?? 'Stavka nije upisana.' })
      return
    }

    const nova: Stavka = {
      id: (upisana as { id: string }).id,
      artikalId: p.artikal_id,
      sifra: artikal.sifra,
      naziv: artikal.naziv,
      jedinica: artikal.osnovna_jedinica,
      nivo: p.nivo,
      sadrziOsn: sadrzi,
      pakovanja: 1,
      stanje: 'sacuvano',
    }
    postaviStavke((prethodne) => [...prethodne, nova])
    postaviZadnjiId(nova.id)
  }

  async function predaj() {
    if (!nacrt) return
    postaviPredaje(true)
    postaviPoruku(null)
    const { error } = await supabase.rpc('predaj_kolicine', { p_dokument: nacrt.id })
    postaviPredaje(false)

    if (error) {
      postaviPoruku(
        error.code === 'BB039'
          ? 'Skeniraj bar jedan artikal prije nego predaš prijem.'
          : `Predaja nije prošla: ${error.message}`,
      )
      return
    }
    postaviNacrt(null)
    postaviStavke([])
    postaviZadnjiId(null)
    postaviSve(false)
    postaviOsvjezi((n) => n + 1)
    postaviPoruku('Prijem je predat. Cijene sa fakture unose se na računaru.')
  }

  const zadnja = useMemo(() => stavke.find((s) => s.id === zadnjiId) ?? null, [stavke, zadnjiId])
  const ukupnoPakovanja = useMemo(() => stavke.reduce((z, s) => z + s.pakovanja, 0), [stavke])

  if (!smijeRaditi) {
    return (
      <div className="prazno" data-testid="prijem-bez-prava">
        Prijem unose magacioner i admin. Tvoja uloga nema to pravo (C17).
      </div>
    )
  }

  // ---------------------------------------------------------------- cijene (računar)
  if (cijene) {
    return (
      <PrijemCijene
        dokumentId={cijene.id}
        partnerNaziv={cijene.partner}
        nalog={nalog}
        naIzlaz={(p) => {
          postaviCijene(null)
          postaviOsvjezi((n) => n + 1)
          if (p) postaviPoruku(p)
        }}
      />
    )
  }

  // ---------------------------------------------------------------- izbor dokumenta
  if (!nacrt) {
    return (
      <div data-testid="prijem-izbor">
        {poruka && (
          <div className="greska" role="status" data-testid="prijem-poruka">
            {poruka}
          </div>
        )}

        {otvoreni.length > 0 && (
          <>
            <h2 className="oznaka odjeljak__naslov">Moji otvoreni nacrti</h2>
            {otvoreni.map((d) => (
              <button
                key={d.id}
                type="button"
                className="red-telefon"
                data-testid="prijem-nacrt"
                onClick={() => void nastavi(d.id, d.partner)}
              >
                <span>{d.partner ?? 'Bez dobavljača'}</span>
                <span className="broj red-telefon__broj">{d.stavki}</span>
              </button>
            ))}
          </>
        )}

        {predati.length > 0 && (
          <>
            <h2 className="oznaka odjeljak__naslov" style={{ marginTop: 'var(--desk-6)' }}>
              Predati — čekaju cijene sa fakture
            </h2>
            {predati.map((d) => (
              <button
                key={d.id}
                type="button"
                className="red-telefon"
                data-testid="prijem-predat"
                onClick={() => postaviCijene({ id: d.id, partner: d.partner })}
              >
                <span>{d.partner ?? 'Bez dobavljača'}</span>
                <span className="broj red-telefon__broj">{d.stavki}</span>
              </button>
            ))}
          </>
        )}

        <h2 className="oznaka odjeljak__naslov" style={{ marginTop: 'var(--desk-6)' }}>
          Novi prijem od dobavljača
        </h2>
        {ucitava ? (
          <div className="ucitavanje">Učitavanje…</div>
        ) : partneri.length === 0 ? (
          <div className="prazno">Nema nijednog aktivnog dobavljača.</div>
        ) : (
          partneri.map((p) => (
            <button
              key={p.id}
              type="button"
              className="red-telefon"
              data-testid="prijem-dobavljac"
              onClick={() => void zapocni(p.id, p.naziv)}
            >
              <span>{p.naziv}</span>
              <span className="red-telefon__strelica" aria-hidden="true">
                →
              </span>
            </button>
          ))
        )}
      </div>
    )
  }

  // ---------------------------------------------------------------- skeniranje
  return (
    <div className="prijem" data-testid="prijem-skeniranje" onClick={vratiFokus}>
      <div className="prijem__zaglavlje">
        <button
          type="button"
          className="veza-tekst"
          data-testid="prijem-nazad"
          onClick={() => {
            postaviNacrt(null)
            postaviSve(false)
          }}
        >
          ← Nacrti
        </button>
        <span className="prijem__partner">{nacrt.partnerNaziv ?? 'Bez dobavljača'}</span>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          void skeniraj(kod)
        }}
      >
        <input
          ref={poljeRef}
          className="scan-polje"
          value={kod}
          onChange={(e) => postaviKod(e.target.value)}
          inputMode="none"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder="Skeniraj bar kod"
          aria-label="Bar kod"
          data-testid="prijem-scan"
        />
      </form>

      {poplava?.vrsta === 'nepoznat' ? (
        <div className="readout readout--nepoznat" role="alert" data-testid="prijem-poplava-nepoznat">
          <div className="readout__oznaka">Nepoznat bar kod</div>
          <div className="readout__kod broj">{poplava.kod}</div>
          <div className="readout__radnje">
            <button type="button" className="dugme dugme--marker" disabled title="Stiže sa katalogom na telefonu">
              Poveži artikal
            </button>
            <button
              type="button"
              className="dugme dugme--obris"
              data-testid="prijem-izbaci"
              onClick={() => postaviPoplavu(null)}
            >
              Izbaci i nastavi
            </button>
          </div>
        </div>
      ) : poplava?.vrsta === 'greska' ? (
        <div className="readout readout--greska" role="alert" data-testid="prijem-poplava-greska">
          <div className="readout__oznaka">Greška</div>
          <div className="readout__poruka">{poplava.poruka}</div>
          <div className="readout__radnje">
            <button type="button" className="dugme dugme--obris" onClick={() => postaviPoplavu(null)}>
              Nastavi
            </button>
          </div>
        </div>
      ) : zadnja ? (
        <div className="readout" data-testid="prijem-readout">
          <div className="readout__kod broj">{zadnja.sifra}</div>
          <div className="readout__naziv">{zadnja.naziv}</div>
          <div className="readout__broj broj" data-testid="prijem-kolicina">
            {kolicina(zadnja.pakovanja * zadnja.sadrziOsn)}
            <span className="readout__jedinica">{zadnja.jedinica}</span>
          </div>
          <div className="readout__pakovanje broj">
            {zadnja.pakovanja} {nivoKratko(zadnja.nivo)} × {kolicina(zadnja.sadrziOsn)} ={' '}
            {kolicina(zadnja.pakovanja * zadnja.sadrziOsn)} {zadnja.jedinica}
          </div>
          <div
            className={`readout__stanje readout__stanje--${zadnja.stanje}`}
            data-testid="prijem-stanje"
            aria-label={
              zadnja.stanje === 'sacuvano' ? 'sačuvano' : zadnja.stanje === 'ceka' ? 'čeka' : 'greška'
            }
          />
        </div>
      ) : (
        <div className="readout readout--prazan" data-testid="prijem-prazan">
          Skeniraj prvi artikal.
        </div>
      )}

      {svePrikazano ? (
        <div className="prijem__lista" data-testid="prijem-sve">
          {stavke.map((s) => (
            <div key={s.id} className="red-telefon red-telefon--stavka">
              <span>
                {s.naziv}
                <span className="broj prijem__pak">
                  {' '}
                  {s.pakovanja} {nivoKratko(s.nivo)} × {kolicina(s.sadrziOsn)}
                </span>
              </span>
              <span className="broj red-telefon__broj">{kolicina(s.pakovanja * s.sadrziOsn)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="prijem__lista">
          {stavke
            .slice(-3)
            .reverse()
            .map((s) => (
              <div key={s.id} className="red-telefon red-telefon--stavka">
                <span>{s.naziv}</span>
                <span className="broj red-telefon__broj">{kolicina(s.pakovanja * s.sadrziOsn)}</span>
              </div>
            ))}
        </div>
      )}

      {poruka && (
        <div className="greska" role="alert" data-testid="prijem-poruka">
          {poruka}
        </div>
      )}

      <div className="donja-traka">
        <button
          type="button"
          className="dugme dugme--sporedno"
          data-testid="prijem-sve-dugme"
          onClick={() => postaviSve((v) => !v)}
        >
          {svePrikazano ? 'Nazad' : `Sve (${stavke.length})`}
        </button>
        <button
          type="button"
          className="dugme dugme--glavno"
          data-testid="prijem-predaj"
          disabled={stavke.length === 0 || predaje}
          onClick={() => void predaj()}
        >
          {predaje ? 'Predaja…' : 'Predaj'}
        </button>
      </div>
      {stavke.length === 0 && (
        <div className="razlog" data-testid="prijem-razlog">
          Skeniraj bar jedan artikal. Ukupno pakovanja: {ukupnoPakovanja}.
        </div>
      )}
    </div>
  )
}
